import { useSelector, useDispatch } from 'react-redux';
import { BranchesOutlined, CloseOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Flex, Form, Typography, Button, message, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setConditions, setSelectedStep, setShowCondition } from '../../../redux/slices/connectorSlice';
import AddConditionForm from './AddConditionForm';
import { getPreviousFormsForConditions } from '../../../utils/workflowHelper';
import { showLoader } from '../../../utils/showloader';
import { deleteFormCondition, deleteWorkflowCondition, getAllFormConditionForWorkflow, getNodeCondition, saveFormCOndition, saveWorkflowCondition } from '../../../services/WorkflowServices';
import ConditionRow from './ConditionRow';
import AddEditConditionForm from '../../form-editor/conditionManager/AddEditConditionForm';


const ConditionManager = ({workflowData={}}) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [nodeConditions, setNodeConditions] = useState([])
    const [showForm, setShowForm] = useState(false)
    const [selectedCondition, setSelectedCondition] = useState({})
    const [formConditions, setFormConditions] = useState([])
    const [selectedForms, setSelectedForms] = useState([])
    const [formElements, setFormElements] = useState([])
    const [selectedConditionType, setSelectedConditionType] = useState("workflow")
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [filteredNodeConditions, setFilteredNodeConditions] = useState([])
    const { selectedStep, conditions, workflow, workflowForms, showCondition } = useSelector(state => state.connector);

    const fetchNodeConditions = async () => {
        showLoader(true)
        let resp = await getNodeCondition(selectedStep.id)
        showLoader(false)
        if(resp.status !== 200){
            message.error(resp.message)
            setNodeConditions([])
            return
        }
        setNodeConditions(resp.data)
    }

    const fetchFormConditions = async () => {
        showLoader(true)
        const response = await getAllFormConditionForWorkflow(workflow.workflowId)
        showLoader(false)
        if(response.data){
            setFormConditions(response.data)
        }

    }

    useEffect(() => {
        let containerEle = document.getElementById("condition-container");
        let stepContainerEle = document.getElementById("step-container");

        if (showCondition) {
            if (containerEle) {
                containerEle.style.display = "flex";
            }
            if(stepContainerEle){
                stepContainerEle.style.display = "none"
            }
            form.setFieldsValue({ name: selectedStep.name, assignTo: selectedStep.assignTo, approvalStep: selectedStep.approvalStep, deadline: selectedStep.deadline })
            let previousNodes = getPreviousFormsForConditions(workflow.nodes, workflow.edges,selectedStep);
            let finalForms = []
            if(previousNodes.length > 0) {
                previousNodes.forEach(node => {
                    if(node?.formData?.formId) {
                        let nodeForm = workflowForms.find(form => form.formId === node.formData.formId)
                        if(nodeForm){
                            finalForms.push(nodeForm)
                        }
                    }
                })
            }
            setSelectedForms(finalForms)
            let elements = []
            finalForms.forEach(form => {
                elements = elements.concat(form.items)
            })
            setFormElements(elements)
            fetchNodeConditions();
            fetchFormConditions();
        } else {
            containerEle.style.display = "none"
            dispatch(setShowCondition(false))
        }
    }, [selectedStep])

    useEffect(() => {
        if (conditions.length > 0) {
            let currentNodeConditions = conditions.filter(condition => condition.nodeId === selectedStep.id)

            setNodeConditions(currentNodeConditions)
        }
    }, [conditions, selectedStep])

    // Filter conditions based on search term
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredNodeConditions(nodeConditions)
            // setFilteredFormConditions(formConditions)
        } else {
            const filteredNode = nodeConditions.filter(condition => 
                condition.name?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            const filteredForm = formConditions.filter(condition => 
                condition.name?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            setFilteredNodeConditions(filteredNode)
            // setFilteredFormConditions(filteredForm)
        }
    }, [searchTerm, nodeConditions, formConditions])

    const closeStepDetails = () => {
        dispatch(setShowCondition(false))
        dispatch(setSelectedStep({}))
    }

    const saveConditions = async (updatedCondition) => {
        let updatedNodeConditions = {...updatedCondition, workflowId: workflowData?.workflowId}
        showLoader(true)
        let saveResponse = await saveWorkflowCondition(updatedNodeConditions)
        setNodeConditions(prev => [...prev, {...updatedCondition}])
        showLoader(false)
        if(saveResponse.status !== 200) {
            message.error(t('Failed to save condition. Please try again'))
            return
        }
        await fetchNodeConditions()
        setShowForm(false)
    }

    const saveFormConditions = async (updatedCondition) => {
        let postData = { ...updatedCondition }
        showLoader(true)
        let saveResponse = await saveFormCOndition(postData)
        showLoader(false)
        if (saveResponse.status !== 200) {
            message.error(saveResponse.message)
            return
        }
        message.success(t('Condition saved successfully'));
        fetchFormConditions();
        setShowForm(false)
    }

    const handleAddNew = (condition={}) => {
        setSelectedCondition(condition)
        setShowForm(true)
    }

    const handleConditionClick = (condition, conditionType="workflow") => {
        setSelectedCondition({...condition})
        setShowForm(true)
        setSelectedConditionType(conditionType)
    }

    const handleCancel = () => {
        setSelectedCondition({})
        setShowForm(false)
        setSelectedConditionType("")
    }

    const handleConditionDelete = async (conditionId) => {
        showLoader(true)
        let deleteResponse = await deleteWorkflowCondition(conditionId)
        showLoader(false)
        if(deleteResponse.status !== 200) {
            message.error(deleteResponse.message)
            return
        }
        message.success(deleteResponse.message)
        await fetchNodeConditions()
    }

    const handleConditionCopy = (condition) => {
        let updatedCondition = {...condition, name: condition.name + " copy"}
        delete updatedCondition.conditionId
        saveConditions(updatedCondition)
    }

    const handleFormConditionCopy = async (condition) => {
        let updatedCondition = {...condition, name: condition.name + " copy"}
        delete updatedCondition.conditionId
        await saveFormConditions(updatedCondition)
    }

    const handleFormConditionDelete = async (conditionId) => {
        showLoader(true)
        let deleteResponse = await deleteFormCondition(conditionId)
        showLoader(false)
        if(deleteResponse.status !== 200) {
            message.error(deleteResponse.message)
            return
        }
        message.success(deleteResponse.message)
        await fetchFormConditions()
    }

    const handleSearchToggle = () => {
        setShowSearch(!showSearch)
        if (showSearch) {
            setSearchTerm("")
        }
    }

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value)
    }

    return (
        <div className='condition-manager-container' id="condition-container" >
            {
                !showForm ?
                    <>
                        <Flex align="center" justify="space-between" className='condition-header'>
                            <Typography.Title level={5} className='margin-0'>{t('Conditional Logic')}</Typography.Title>
                            <Flex align="center" gap={8}>
                                <CloseOutlined className="icon-medium" onClick={closeStepDetails} />
                            </Flex>
                        </Flex>
                        
                        {/* Sticky Actions Section */}
                        <Flex className='condition-actions-sticky justify-between'>
                            {nodeConditions.length > 0 && (
                                <div className='flex justify-between items-center'>
                                    <Button className='bordered-button' icon={<PlusOutlined />} onClick={handleAddNew}>{t('Add new condition')}</Button>
                                    {/* <SearchOutlined className="icon-medium" onClick={handleSearchToggle} style={{ cursor: 'pointer' }} /> */}
                                </div>
                            )}
                                <div className='mx-2'>
                                    <Input
                                        placeholder={t('Search conditions...')}
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        prefix={<SearchOutlined />}
                                        allowClear
                                    />
                                </div>
                        </Flex>
                        
                        <div className='condition-details-body my-2'>
                            {
                                filteredNodeConditions.length === 0 && nodeConditions.length === 0 ?
                                    <div className='add-condition-container' onClick={handleAddNew}>
                                        <div className='add-condition-node'>
                                            <BranchesOutlined />
                                        </div>
                                        <div className="node-title text-weight-400">{t('Add new condition')}</div>
                                    </div>
                                :
                                    <>
                                        {
                                            filteredNodeConditions.map(condition => {
                                                return <ConditionRow key={condition.conditionId} formElements={formElements} selectedForms={selectedForms} condition={condition} handleConditionClick={handleConditionClick} handleConditionDelete={handleConditionDelete} handleConditionCopy={handleConditionCopy} type="workflow" />
                                            })
                                        }
                                    </>
                            }
                            {/* {
                                filteredFormConditions.length > 0 ?
                                <>
                                    <Typography.Title level={5} className='margin-0'>Form Conditions</Typography.Title>
                                        {
                                            filteredFormConditions.map(condition => {
                                                return <FormConditionRow key={condition.conditionId} formElements={formElements} condition={condition} handleConditionClick={handleConditionClick} handleConditionDelete={handleFormConditionDelete} handleConditionCopy={handleFormConditionCopy} type="form" />
                                            })
                                        }
                                    </>
                                    : null
                            } */}


                        </div>
                    </>
                    : 
                    <>
                        {
                            selectedConditionType === "workflow" ? 
                            <AddConditionForm selectedStep={selectedStep} conditionType={selectedConditionType} selectedForms={selectedForms} selectedCondition={selectedCondition} setShowForm={setShowForm} saveConditions={saveConditions} handleCancel={handleCancel} />
                            : <AddEditConditionForm selectedCondition={selectedCondition} setShowForm={setShowForm} formElements={formElements} saveConditions={saveFormConditions} handleCancel={handleCancel}  />
                        }
                    </>
            }
        </div >
    )
}

export default ConditionManager